import { useState } from "react";

interface Option {
  id: string; // A, B, C, D
  text: string;
}

interface QuizBarsProps {
  question: string;
  options: Option[];
  correctId: string;
  educatorMode?: boolean;
}

/**
 * QuizBars renders the multiple-choice UI plus the reveal bar-chart in the
 * same component for the sake of the prototype.
 */
export default function QuizBars({
  question,
  options,
  correctId,
  educatorMode = false,
}: QuizBarsProps) {
  // local state – answer counts. seeded with zeros.
  const [counts, setCounts] = useState(() =>
    Object.fromEntries(options.map((o) => [o.id, 0])) as Record<string, number>
  );
  const [phase, setPhase] = useState<"live" | "reveal">("live");
  const [myAnswer, setMyAnswer] = useState<string | null>(null);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const submitAnswer = (id: string) => {
    if (phase !== "live" || myAnswer) return;
    setMyAnswer(id);
    setCounts((prev) => ({ ...prev, [id]: prev[id] + 1 }));
  };

  const reveal = () => setPhase("reveal");

  return (
    <div className="w-full max-w-xl space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {question}
      </h2>

      {phase === "live" && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {options.map((o) => {
            const locked = !!myAnswer;
            const selected = myAnswer === o.id;
            return (
              <button
                key={o.id}
                disabled={locked}
                onClick={() => submitAnswer(o.id)}
                className={
                  "flex h-12 items-center justify-center rounded-lg border bg-white px-4 text-sm font-medium transition-colors " +
                  (selected
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-70 disabled:hover:bg-white")
                }
              >
                <span className="mr-2 font-bold">{o.id}</span> {o.text}
              </button>
            );
          })}
        </div>
      )}

      {phase === "reveal" && (
        <div className="space-y-2">
          {options.map((o) => {
            const value = counts[o.id] || 0;
            const pct = total === 0 ? 0 : (value / total) * 100;
            const correct = o.id === correctId;

            return (
              <div key={o.id} className="space-y-1">
                <div className="flex justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span>
                    <span className="mr-2 font-bold">{o.id}</span> {o.text}
                  </span>
                  <span>{value}</span>
                </div>
                <div className="h-4 w-full rounded-lg bg-gray-200 dark:bg-gray-700">
                  <div
                    className={
                      "h-full rounded-lg transition-all duration-700 " +
                      (correct
                        ? "bg-[color:var(--c-success,#059669)]"
                        : "bg-gray-400 dark:bg-gray-600")
                    }
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* educator controls */}
      {educatorMode && (
        <div className="flex gap-2 pt-2">
          {phase === "live" ? (
            <button
              onClick={reveal}
              className="rounded-md bg-primary px-3 py-1.5 text-white hover:bg-primary-dark focus:outline-hidden focus:ring-3 focus:ring-primary"
            >
              Reveal Results
            </button>
          ) : (
            <button
              onClick={() => {
                // reset
                setCounts(Object.fromEntries(options.map((o) => [o.id, 0])));
                setMyAnswer(null);
                setPhase("live");
              }}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-3 focus:ring-primary"
            >
              New Question
            </button>
          )}
        </div>
      )}

      {/* student result badge */}
      {!educatorMode && phase === "reveal" && myAnswer && (
        <div className="text-center text-lg font-semibold">
          {myAnswer === correctId ? (
            <span className="text-[color:var(--c-success,#059669)]">Correct 🎉</span>
          ) : (
            <span className="text-red-500">Incorrect</span>
          )}
        </div>
      )}
    </div>
  );
}
